import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthShell = ({ kicker, title, children }) => (
  <div data-theme="dark" data-atelier="true" className="min-h-screen surface-obsidian flex items-center justify-center p-6 relative">
    <div className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-lime-500 to-transparent z-50" />
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
        backgroundSize: '72px 72px',
        maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 0%, transparent 75%)',
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse 45% 35% at 50% 45%, rgba(255,84,54,0.1) 0%, transparent 70%)',
      }}
    />
    <div className="w-full max-w-md relative z-10">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-5">
          <div
            className="w-9 h-9 bg-lime-500 rounded-md flex items-center justify-center"
            style={{ boxShadow: '0 0 18px rgba(255,84,54,0.45)' }}
          >
            <Sparkles className="w-4 h-4 text-obsidian-900" strokeWidth={2.5} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-semibold text-base text-white tracking-tight">Reelscript</span>
            <span className="label-micro text-lime-500">AI</span>
          </div>
        </div>
        {kicker && (
          <div className="label-micro text-lime-500 mb-3 flex items-center justify-center gap-3">
            <span className="h-px w-6 bg-lime-500/60" />
            {kicker}
            <span className="h-px w-6 bg-lime-500/60" />
          </div>
        )}
        <h1 className="font-display font-semibold text-white text-3xl md:text-4xl tracking-tighter leading-tight">
          {title}
        </h1>
      </div>
      <div className="surface-obsidian-elevated rounded-2xl p-7 md:p-8">
        {children}
      </div>
    </div>
  </div>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('請輸入郵箱地址'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email, origin_url: window.location.origin });
      setSent(true);
      toast.success('重設郵件已發送');
    } catch (error) {
      toast.error(error.response?.data?.detail || '發送失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell kicker="EMAIL · SENT" title="郵件已發送">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-lime-500/10 border border-lime-500/30 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-lime-500" strokeWidth={2} />
          </div>
          <p className="text-obsidian-200 text-sm leading-relaxed mb-6">
            如果 <span className="text-white font-medium">{email}</span> 已註冊，您將收到密碼重設郵件。
            <br />請檢查您的收件箱（包括垃圾郵件）。
          </p>
          <Button
            onClick={() => navigate('/')}
            className="w-full rounded-full h-11 btn-neon font-semibold text-sm"
          >
            返回登入
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell kicker="FORGOT · PASSWORD" title="忘記密碼">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 label-micro text-obsidian-300 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        返回登入
      </button>

      <div className="mb-6 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-lime-500/10 border border-lime-500/30 flex items-center justify-center">
          <Mail className="w-6 h-6 text-lime-500" strokeWidth={2} />
        </div>
        <p className="text-obsidian-200 text-sm">輸入您的郵箱地址，我們將發送密碼重設連結給您。</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label className="label-micro text-obsidian-300 mb-2 block">EMAIL · ADDRESS</Label>
          <Input
            type="email" placeholder="your@email.com" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg h-11 bg-obsidian-800 border-obsidian-600 text-white text-sm focus:border-lime-500/60 placeholder:text-obsidian-400"
            data-testid="forgot-email-input"
          />
        </div>
        <Button
          type="submit" disabled={loading}
          className="w-full rounded-full h-11 btn-neon font-semibold text-sm"
          data-testid="forgot-submit-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />發送中…</> : '發送重設郵件'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default ForgotPassword;
